import React, { useState, useRef, useEffect } from 'react';
import styled from '@emotion/styled';
import ReactMarkdown from 'react-markdown';
import { 
    color_shades_dark, 
    color_shades_darker, 
    color_shades_darkest, 
    color_colors_ocean, 
    silver_two,
    color_variants_ocean_light_2,
    color_colors_growth,
    color_colors_growth_background,
    color_colors_decline,
    color_variants_decline_light_1,
    color_variants_red_2,
    pale_grey
} from '../../constants/colors';
import { DetectedVariable, VariableFieldValue, VariableValueState } from '../../hooks/useResponseVariableTester';
import { chatWithResponseVariableAssistant, GenAIChatTurn } from '../../api/controllers/responseVariable';
import { useApplicationCredentials } from '../../hooks/useApplicationCredentials';

const copyIcon = require('../../content/images/copy-text.svg');
const sendIcon = require('../../content/images/simulator/send.svg');

interface ResponseVariableTesterUIProps {
    // State from hook
    inputText: string;
    setInputText: (text: string) => void;
    variableValues: Map<string, VariableFieldValue>;
    isLoading: boolean;
    replacedText: string | null;
    error: string | null;
    detectedVariables: DetectedVariable[];
    
    // Handlers from hook
    handleValueStateChange: (fullReference: string, state: VariableValueState) => void;
    handleStringValueChange: (fullReference: string, value: string) => void;
    handleNumberValueChange: (fullReference: string, value: number) => void;
    handleBooleanValueChange: (fullReference: string, value: boolean) => void;
    getCategoryColor: (category: string) => string;
    
    // Optional customization
    placeholder?: string;
    docsUrl?: string;
}

const ResponseVariableTesterUI: React.FC<ResponseVariableTesterUIProps> = ({
    inputText,
    setInputText,
    variableValues,
    isLoading,
    replacedText,
    error,
    detectedVariables,
    handleValueStateChange,
    handleStringValueChange,
    handleNumberValueChange,
    handleBooleanValueChange,
    getCategoryColor,
    placeholder = "Hello {{session.name}}, your order total is {{constant.currency}}{{session.order.total}}.",
    docsUrl
}) => {
    // Get credentials automatically from the user's organizations/applications
    const { applicationId, applicationSecret } = useApplicationCredentials();

    // AI Chat state
    const [chatHistory, setChatHistory] = useState<GenAIChatTurn[]>([]);
    const [chatInput, setChatInput] = useState('');
    const [isChatLoading, setIsChatLoading] = useState(false);
    const chatHistoryRef = useRef<HTMLDivElement>(null);

    // Auto-scroll to bottom when chat history updates
    useEffect(() => {
        if (chatHistoryRef.current) {
            chatHistoryRef.current.scrollTop = chatHistoryRef.current.scrollHeight;
        }
    }, [chatHistory]);

    const handleSendMessage = async () => {
        if (!chatInput.trim() || isChatLoading || !applicationId || !applicationSecret) return;

        // Build the user message with context about current input/result/variables
        let messageText = chatInput.trim();
        const hasContext = inputText || replacedText || variableValues.size > 0;
        if (hasContext) {
            let context = '\n\n---\nContext:';
            if (inputText) {
                context += `\nCurrent Input: ${inputText}`;
            }
            if (replacedText) {
                context += `\nCurrent Result: ${replacedText}`;
            }
            if (variableValues.size > 0) {
                context += '\nConfigured Variables:';
                variableValues.forEach((fieldValue, varName) => {
                    let valueDisplay: string;
                    switch (fieldValue.state) {
                        case 'string':
                            valueDisplay = `"${fieldValue.stringValue}"`;
                            break;
                        case 'number':
                            valueDisplay = String(fieldValue.numberValue);
                            break;
                        case 'boolean':
                            valueDisplay = String(fieldValue.booleanValue);
                            break;
                        case 'null':
                            valueDisplay = 'null';
                            break;
                        case 'undefined':
                            valueDisplay = 'undefined';
                            break;
                        default:
                            valueDisplay = 'unknown';
                    }
                    context += `\n  - ${varName}: ${valueDisplay} (${fieldValue.state})`;
                });
            }
            messageText += context;
        }

        const userMessage: GenAIChatTurn = { role: 'user', text: messageText };
        // Show only the user's question in the UI, not the context
        const displayMessage: GenAIChatTurn = { role: 'user', text: chatInput.trim() };
        const newHistory = [...chatHistory, displayMessage];
        const historyForApi = [...chatHistory, userMessage];
        setChatHistory(newHistory);
        setChatInput('');
        setIsChatLoading(true);

        try {
            const response = await chatWithResponseVariableAssistant({
                applicationId,
                applicationSecret,
                conversationHistory: historyForApi
            });
            
            if (response.data?.response) {
                const assistantMessage: GenAIChatTurn = { role: 'assistant', text: response.data.response };
                setChatHistory([...newHistory, assistantMessage]);
            }
        } catch (err) {
            const errorMessage: GenAIChatTurn = { role: 'assistant', text: 'Sorry, I encountered an error. Please try again.' };
            setChatHistory([...newHistory, errorMessage]);
        } finally {
            setIsChatLoading(false);
        }
    };

    const handleChatKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const formatChatHistory = () => {
        if (chatHistory.length === 0) {
            return 'Ask me anything about response variables...\n\nExamples:\n• How do I use session variables?\n• What\'s the syntax for nested objects?\n• How do constant variables work?';
        }
        return chatHistory.map(turn => 
            `${turn.role === 'user' ? 'You' : 'Assistant'}: ${turn.text}`
        ).join('\n\n');
    };

    const copyChatHistory = () => {
        navigator.clipboard.writeText(formatChatHistory());
    };

    return (
        <Container>
            {docsUrl && (
                <DocsLink href={docsUrl} target="_blank" rel="noopener noreferrer">
                    View Response Variables Documentation →
                </DocsLink>
            )}

            <TopSection>
                <ChatPane>
                    <InputHeader>
                        <SectionTitle>AI Assistant</SectionTitle>
                        <ChatHeaderButtons>
                            <CopyButton 
                                onClick={() => setChatHistory([])}
                                disabled={chatHistory.length === 0}
                                title="Start over"
                            >
                                Start Over
                            </CopyButton>
                            <CopyButton 
                                onClick={copyChatHistory}
                                disabled={chatHistory.length === 0}
                                title="Copy chat history"
                            >
                                <img src={copyIcon} alt="Copy" />
                                Copy
                            </CopyButton>
                        </ChatHeaderButtons>
                    </InputHeader>
                    <SectionDescription>
                        Ask AI how to use response variables.
                    </SectionDescription>
                    <ChatHistoryBox ref={chatHistoryRef}>
                        {chatHistory.length === 0 ? (
                            <ChatPlaceholder>
                                Ask me anything about response variables...
                                <br /><br />
                                Examples:<br />
                                • How do I use session variables?<br />
                                • What's the syntax for nested objects?<br />
                                • How do constant variables work?
                            </ChatPlaceholder>
                        ) : (
                            chatHistory.map((turn, index) => (
                                <ChatMessage key={index} $isUser={turn.role === 'user'}>
                                    <ChatMessageLabel>{turn.role === 'user' ? 'You' : 'Assistant'}</ChatMessageLabel>
                                    {turn.role === 'assistant' ? (
                                        <MarkdownContent>
                                            <ReactMarkdown linkTarget="_blank">{turn.text}</ReactMarkdown>
                                        </MarkdownContent>
                                    ) : (
                                        <UserMessageText>{turn.text}</UserMessageText>
                                    )}
                                </ChatMessage>
                            ))
                        )}
                    </ChatHistoryBox>
                    <ChatInputRow>
                        <ChatInput
                            type="text"
                            value={chatInput}
                            onChange={(e) => setChatInput(e.target.value)}
                            onKeyDown={handleChatKeyDown}
                            placeholder="Ask about response variables..."
                            disabled={isChatLoading}
                        />
                        <SendButton 
                            onClick={handleSendMessage}
                            disabled={!chatInput.trim() || isChatLoading}
                            title="Send message"
                        >
                            {isChatLoading ? <LoadingSpinner /> : <img src={sendIcon} alt="Send" />}
                        </SendButton>
                    </ChatInputRow>
                </ChatPane>
                <InputPane>
                    <InputHeader>
                        <SectionTitle>Input Text</SectionTitle>
                        <CopyButton 
                            onClick={() => navigator.clipboard.writeText(inputText)}
                            disabled={!inputText.trim()}
                            title="Copy to clipboard"
                        >
                            <img src={copyIcon} alt="Copy" />
                            Copy
                        </CopyButton>
                    </InputHeader>
                    <SectionDescription>
                        Enter text containing response variables. Variables are automatically detected.
                    </SectionDescription>
                    <TextAreaInput
                        value={inputText}
                        onChange={(e) => setInputText(e.target.value)}
                        placeholder={placeholder}
                        rows={6}
                    />
                </InputPane>
                <ResultPane>
                    <SectionTitle>Result {isLoading && <LoadingIndicator>(updating...)</LoadingIndicator>}</SectionTitle>
                    <SectionDescription>
                        Replaced text will appear here automatically.
                    </SectionDescription>
                    {error ? (
                        <ErrorMessage>{error}</ErrorMessage>
                    ) : replacedText !== null ? (
                        <ResultTextArea readOnly value={replacedText} rows={6} />
                    ) : (
                        <ResultPlaceholder>Enter text with variables to see the result...</ResultPlaceholder>
                    )}
                </ResultPane>
            </TopSection>

            {detectedVariables.length > 0 && (
                <Section>
                    <SectionTitle>Detected Variables ({detectedVariables.length})</SectionTitle>
                    <SectionDescription>
                        Configure values for each detected variable.
                    </SectionDescription>
                    <VariablesList>
                        {detectedVariables.map((variable) => {
                            const fieldValue = variableValues.get(variable.fullReference) || { 
                                state: 'string' as VariableValueState, 
                                stringValue: '', 
                                numberValue: 0, 
                                booleanValue: false 
                            };
                            return (
                                <VariableField key={variable.fullReference}>
                                    <VariableHeader>
                                        <CategoryBadge style={{ backgroundColor: getCategoryColor(variable.category) }}>
                                            {variable.category}
                                        </CategoryBadge>
                                        <VariableName>{variable.fullReference}</VariableName>
                                    </VariableHeader>
                                    <VariableControls>
                                        <StateSelector>
                                            <StateButton 
                                                className={fieldValue.state === 'string' ? 'active' : ''}
                                                onClick={() => handleValueStateChange(variable.fullReference, 'string')}
                                            >
                                                String
                                            </StateButton>
                                            <StateButton 
                                                className={fieldValue.state === 'number' ? 'active' : ''}
                                                onClick={() => handleValueStateChange(variable.fullReference, 'number')}
                                            >
                                                Number
                                            </StateButton>
                                            <StateButton 
                                                className={fieldValue.state === 'boolean' ? 'active' : ''}
                                                onClick={() => handleValueStateChange(variable.fullReference, 'boolean')}
                                            >
                                                Boolean
                                            </StateButton>
                                            <StateButton 
                                                className={fieldValue.state === 'null' ? 'active' : ''}
                                                onClick={() => handleValueStateChange(variable.fullReference, 'null')}
                                            >
                                                null
                                            </StateButton>
                                            <StateButton 
                                                className={fieldValue.state === 'undefined' ? 'active' : ''}
                                                onClick={() => handleValueStateChange(variable.fullReference, 'undefined')}
                                            >
                                                undefined
                                            </StateButton>
                                        </StateSelector>
                                        {fieldValue.state === 'string' && (
                                            <ValueInput
                                                type="text"
                                                value={fieldValue.stringValue}
                                                onChange={(e) => handleStringValueChange(variable.fullReference, e.target.value)}
                                                placeholder="Enter value..."
                                            />
                                        )}
                                        {fieldValue.state === 'number' && (
                                            <ValueInput
                                                type="number"
                                                value={fieldValue.numberValue}
                                                onChange={(e) => handleNumberValueChange(variable.fullReference, parseFloat(e.target.value) || 0)}
                                                placeholder="Enter number..."
                                            />
                                        )}
                                        {fieldValue.state === 'boolean' && (
                                            <BooleanSelector>
                                                <BooleanButton
                                                    className={fieldValue.booleanValue === true ? 'active-true' : ''}
                                                    onClick={() => handleBooleanValueChange(variable.fullReference, true)}
                                                >
                                                    true
                                                </BooleanButton>
                                                <BooleanButton
                                                    className={fieldValue.booleanValue === false ? 'active-false' : ''}
                                                    onClick={() => handleBooleanValueChange(variable.fullReference, false)}
                                                >
                                                    false
                                                </BooleanButton>
                                            </BooleanSelector>
                                        )}
                                        {(fieldValue.state === 'null' || fieldValue.state === 'undefined') && (
                                            <ValuePlaceholder>
                                                {fieldValue.state === 'null' ? 'Value will be null' : 'Value will be undefined (not sent)'}
                                            </ValuePlaceholder>
                                        )}
                                    </VariableControls>
                                </VariableField>
                            );
                        })}
                    </VariablesList>
                </Section>
            )}
        </Container>
    );
};

// Styles
const Container = styled.div``;

const DocsLink = styled.a`
    display: inline-block;
    margin-bottom: 20px;
    font-size: 14px;
    color: ${color_colors_ocean};
    text-decoration: none;
    font-weight: 500;
    
    &:hover {
        text-decoration: underline;
    }
`;

const TopSection = styled.div`
    display: flex;
    gap: 24px;
    margin-bottom: 24px;
`;

const InputPane = styled.div`
    flex: 1;
    min-width: 0;
`;

const ResultPane = styled.div`
    flex: 1;
    min-width: 0;
`;

const ChatPane = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
`;

const ChatHeaderButtons = styled.div`
    display: flex;
    gap: 8px;
`;

const ChatHistoryBox = styled.div`
    width: 100%;
    min-height: 100px;
    max-height: 200px;
    padding: 12px 16px;
    border: 1px solid ${silver_two};
    border-radius: 8px;
    font-size: 14px;
    color: ${color_shades_darkest};
    background: ${pale_grey};
    overflow-y: auto;
    overflow-x: hidden;
    box-sizing: border-box;
    flex: 1;
`;

const ChatPlaceholder = styled.div`
    color: ${color_shades_dark};
    font-style: italic;
    font-size: 13px;
`;

const ChatMessage = styled.div<{ $isUser: boolean }>`
    margin-bottom: 12px;
    padding-bottom: 12px;
    border-bottom: 1px solid ${silver_two};
    
    &:last-child {
        margin-bottom: 0;
        padding-bottom: 0;
        border-bottom: none;
    }
`;

const ChatMessageLabel = styled.div`
    font-size: 11px;
    font-weight: 600;
    color: ${color_shades_darker};
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 4px;
`;

const UserMessageText = styled.div`
    font-size: 14px;
    color: ${color_shades_darkest};
`;

const MarkdownContent = styled.div`
    font-size: 14px;
    color: ${color_shades_darkest};
    
    p {
        margin: 0 0 8px 0;
        
        &:last-child {
            margin-bottom: 0;
        }
    }
    
    code {
        background: white;
        padding: 2px 6px;
        border-radius: 4px;
        font-family: 'Consolas', 'Monaco', monospace;
        font-size: 13px;
        border: 1px solid ${silver_two};
    }
    
    pre {
        background: white;
        padding: 12px;
        border-radius: 6px;
        overflow-x: auto;
        border: 1px solid ${silver_two};
        margin: 8px 0;
        
        code {
            background: none;
            padding: 0;
            border: none;
        }
    }
    
    ul, ol {
        margin: 8px 0;
        padding-left: 20px;
    }
    
    li {
        margin-bottom: 4px;
    }
    
    a {
        color: ${color_colors_ocean};
        text-decoration: none;
        
        &:hover {
            text-decoration: underline;
        }
    }
    
    strong {
        font-weight: 600;
    }
`;

const ChatInputRow = styled.div`
    display: flex;
    gap: 8px;
    margin-top: 8px;
`;

const ChatInput = styled.input`
    flex: 1;
    padding: 10px 12px;
    border: 1px solid ${silver_two};
    border-radius: 6px;
    font-size: 14px;
    color: ${color_shades_darkest};
    background: white;
    box-sizing: border-box;
    
    &::placeholder {
        color: ${color_shades_dark};
        font-style: italic;
    }
    
    &:focus {
        outline: none;
        border-color: ${color_colors_ocean};
    }
    
    &:disabled {
        background: ${pale_grey};
        cursor: not-allowed;
    }
`;

const SendButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border: 1px solid ${color_colors_ocean};
    background: ${color_colors_ocean};
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.15s ease;
    
    img {
        width: 18px;
        height: 18px;
        filter: brightness(0) invert(1);
    }
    
    &:hover:not(:disabled) {
        background: ${color_shades_darkest};
        border-color: ${color_shades_darkest};
    }
    
    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const LoadingSpinner = styled.div`
    width: 18px;
    height: 18px;
    border: 2px solid white;
    border-top-color: transparent;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    
    @keyframes spin {
        to {
            transform: rotate(360deg);
        }
    }
`;

const InputHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
`;

const CopyButton = styled.button`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border: 1px solid ${silver_two};
    background: white;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;
    color: ${color_shades_darker};
    cursor: pointer;
    transition: all 0.15s ease;
    
    img {
        width: 14px;
        height: 14px;
    }
    
    &:hover:not(:disabled) {
        background: ${pale_grey};
        border-color: ${color_colors_ocean};
        color: ${color_colors_ocean};
    }
    
    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const Section = styled.div`
    margin-bottom: 24px;
    
    &:last-child {
        margin-bottom: 0;
    }
`;

const SectionTitle = styled.h4`
    font-size: 16px;
    font-weight: 600;
    color: ${color_shades_darkest};
    margin: 0;
`;

const SectionDescription = styled.p`
    font-size: 14px;
    color: ${color_shades_darker};
    margin: 8px 0 16px 0;
`;

const LoadingIndicator = styled.span`
    font-size: 12px;
    font-weight: 400;
    color: ${color_shades_darker};
    font-style: italic;
    margin-left: 8px;
`;

const TextAreaInput = styled.textarea`
    width: 100%;
    min-height: 100px;
    padding: 12px 16px;
    border: 1px solid ${silver_two};
    border-radius: 8px;
    font-family: 'Muli', sans-serif;
    font-size: 14px;
    color: ${color_shades_darkest};
    resize: vertical;
    box-sizing: border-box;
    
    &::placeholder {
        color: ${color_shades_dark};
        font-style: italic;
    }
    
    &:focus {
        outline: none;
        border-color: ${color_colors_ocean};
        box-shadow: 0 0 0 2px ${color_variants_ocean_light_2};
    }
`;

const ResultTextArea = styled.textarea`
    width: 100%;
    min-height: 100px;
    padding: 12px 16px;
    border: 1px solid ${color_colors_growth};
    border-radius: 8px;
    font-family: 'Muli', sans-serif;
    font-size: 14px;
    color: ${color_shades_darkest};
    background: ${color_colors_growth_background};
    resize: vertical;
    box-sizing: border-box;
    
    &:focus {
        outline: none;
    }
`;

const ResultPlaceholder = styled.div`
    width: 100%;
    min-height: 100px;
    padding: 12px 16px;
    border: 1px dashed ${silver_two};
    border-radius: 8px;
    font-size: 14px;
    color: ${color_shades_dark};
    font-style: italic;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
`;

const ErrorMessage = styled.div`
    padding: 12px 16px;
    background: ${color_variants_decline_light_1};
    border: 1px solid ${color_variants_red_2};
    border-radius: 8px;
    color: ${color_colors_decline};
    font-size: 14px;
`;

const VariablesList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    max-height: 450px;
    overflow-y: auto;
`;

const VariableField = styled.div`
    background: ${pale_grey};
    border: 1px solid ${silver_two};
    border-radius: 8px;
    padding: 16px;
`;

const VariableHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
`;

const CategoryBadge = styled.span`
    display: inline-block;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 600;
    color: white;
    text-transform: uppercase;
    letter-spacing: 0.5px;
`;

const VariableName = styled.span`
    font-family: 'Consolas', 'Monaco', monospace;
    font-size: 14px;
    font-weight: 600;
    color: ${color_shades_darkest};
`;

const VariableControls = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const StateSelector = styled.div`
    display: flex;
    gap: 4px;
    flex-wrap: wrap;
`;

const StateButton = styled.button`
    padding: 6px 12px;
    border: 1px solid ${silver_two};
    background: white;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;
    color: ${color_shades_darker};
    cursor: pointer;
    transition: all 0.15s ease;
    
    &:hover {
        background: ${pale_grey};
    }
    
    &.active {
        background: ${color_colors_ocean};
        color: white;
        border-color: ${color_colors_ocean};
    }
`;

const BooleanSelector = styled.div`
    display: flex;
    gap: 8px;
    width: fit-content;
`;

const BooleanButton = styled.button`
    padding: 10px 24px;
    border: 1px solid ${silver_two};
    background: white;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 600;
    color: ${color_shades_darker};
    cursor: pointer;
    transition: all 0.15s ease;
    font-family: 'Consolas', 'Monaco', monospace;
    
    &:hover {
        background: ${pale_grey};
    }
    
    &.active-true {
        background: ${color_colors_growth};
        color: white;
        border-color: ${color_colors_growth};
    }
    
    &.active-false {
        background: ${color_colors_decline};
        color: white;
        border-color: ${color_colors_decline};
    }
`;

const ValueInput = styled.input`
    width: 100%;
    padding: 10px 12px;
    border: 1px solid ${silver_two};
    border-radius: 6px;
    font-size: 14px;
    color: ${color_shades_darkest};
    background: white;
    box-sizing: border-box;
    
    &::placeholder {
        color: ${color_shades_dark};
        font-style: italic;
    }
    
    &:focus {
        outline: none;
        border-color: ${color_colors_ocean};
    }
`;

const ValuePlaceholder = styled.div`
    padding: 10px 12px;
    background: white;
    border: 1px dashed ${silver_two};
    border-radius: 6px;
    font-size: 14px;
    color: ${color_shades_dark};
    font-style: italic;
`;

export default ResponseVariableTesterUI;
